/**
 * Date utility functions - Abstraction layer for date operations
 * This module provides a unified interface for date operations, abstracting away
 * the underlying date library (currently dayjs) to make it easier to swap implementations
 */

import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

// Initialize dayjs plugins
dayjs.extend(relativeTime);
dayjs.locale('es');

// Internal type for date inputs (includes Dayjs for internal operations)
type DateInput = string | Date | Dayjs | null | undefined;

/**
 * Parse a date from various input formats
 * @param date - Date string, Date object, or dayjs object
 * @returns Parsed date object (internal representation)
 */
export const parseDate = (date: DateInput) => {
  return dayjs(date);
};

/**
 * Get current date/time
 * @returns Current date object
 */
export const now = () => {
  return dayjs();
};

/**
 * Convert a date to a native JavaScript Date object
 * @param date - Date string, Date object, or dayjs object
 * @returns Native Date object
 */
export const toNativeDate = (date: DateInput): Date => {
  return dayjs(date).toDate();
};

/**
 * Format a date to DD/MM/YYYY
 * @param date - Date to format
 * @returns Formatted date string
 */
export const formatDate = (date: DateInput): string => {
  return dayjs(date).format('DD/MM/YYYY');
};

/**
 * Format a date to YYYY-MM-DD (ISO format)
 * @param date - Date to format
 * @returns Formatted date string
 */
export const formatDateISO = (date: DateInput): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Format a date to YYYY-MM-DD (local time)
 * @param date - Date to format
 * @returns Formatted date string in local time
 */
export const formatDateUTC = (date: DateInput): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Format a date to locale date string (es-AR)
 * @param date - Date to format
 * @returns Formatted date string in es-AR locale
 */
export const formatDateLocale = (date: DateInput): string => {
  if (!date) return '';

  // If it's an ISO date string (with or without time), parse it as local date to avoid timezone issues
  if (typeof date === 'string') {
    // Extract just the date part (YYYY-MM-DD) from ISO strings like "2025-10-22" or "2025-10-22T00:00:00"
    const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      const result = new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('es-AR');
      return result;
    }
  }

  const result = dayjs(date).toDate().toLocaleDateString('es-AR');
  return result;
};

/**
 * Format a date-time to DD/MM/YYYY HH:mm in local timezone.
 * For ISO-like strings without timezone, it preserves local date/time values.
 * @param date - Date to format
 * @returns Formatted date-time string
 */
export const formatDateTimeLocale = (date: DateInput): string => {
  if (!date) return '';

  if (typeof date === 'string') {
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(date);
    const localDateTimeMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);

    if (localDateTimeMatch && !hasTimezone) {
      const [, year, month, day, hour = '00', minute = '00'] = localDateTimeMatch;
      return dayjs(new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))).format('DD/MM/YYYY HH:mm');
    }
  }

  const parsed = dayjs(date);
  if (!parsed.isValid()) return '';

  return parsed.format('DD/MM/YYYY HH:mm');
};

/**
 * Format a date to locale time string (es-AR)
 * @param date - Date to format
 * @returns Formatted time string in es-AR locale
 */
export const formatTimeLocale = (date: DateInput): string => {
  return dayjs(date).toDate().toLocaleTimeString('es-AR');
};

/**
 * Get relative time from now (e.g., "hace 2 días")
 * @param date - Date to compare
 * @returns Relative time string in Spanish
 */
export const fromNow = (date: DateInput): string => {
  return dayjs(date).fromNow();
};

/**
 * Calculate difference between two dates
 * @param date1 - First date (usually later)
 * @param date2 - Second date (usually earlier)
 * @param unit - Unit of time ('days', 'hours', 'minutes', etc.)
 * @returns Difference in the specified unit
 */
export const dateDiff = (
  date1: DateInput,
  date2: DateInput,
  unit: 'day' | 'days' | 'hour' | 'hours' | 'minute' | 'minutes' | 'second' | 'seconds' = 'days'
): number => {
  return dayjs(date1).diff(dayjs(date2), unit);
};

/**
 * Add time to a date
 * @param date - Base date
 * @param amount - Amount to add
 * @param unit - Unit of time to add
 * @returns New date with added time (as Dayjs object for chaining)
 */
export const addToDate = (
  date: DateInput,
  amount: number,
  unit: 'day' | 'days' | 'hour' | 'hours' | 'minute' | 'minutes' | 'second' | 'seconds' = 'days'
) => {
  return dayjs(date).add(amount, unit);
};

/**
 * Check if a date is valid
 * @param date - Date to validate
 * @returns True if date is valid
 */
export const isValidDate = (date: DateInput): boolean => {
  return dayjs(date).isValid();
};

/**
 * Get timestamp (milliseconds since epoch)
 * @param date - Date to convert
 * @returns Timestamp in milliseconds
 */
export const toTimestamp = (date: DateInput): number => {
  return dayjs(date).valueOf();
};

/**
 * Format a custom date pattern
 * @param date - Date to format
 * @param format - Format pattern (dayjs format)
 * @returns Formatted date string
 */
export const formatCustom = (date: DateInput, format: string): string => {
  return dayjs(date).format(format);
};
